import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { WebAppUser } from '../inteface/telegram';
import { environment } from '../../environments/environment';

interface TgButton {
  show(): void;
  hide(): void;
  setText(text: string): void;
  onClick(fn: Function): void;
  offClick(fn: Function): void;
  enable(): void;
  disable(): void;
}

interface TelegramInvoice {
  title: string;
  description: string;
  payload: string;
  provider_token: string;
  currency: string;
  prices: Array<{
    label: string;
    amount: number;
  }>;
  max_tip_amount?: number;
  suggested_tip_amounts?: number[];
  provider_data?: string;
  photo_url?: string;
  photo_size?: number;
  photo_width?: number;
  photo_height?: number;
  need_name?: boolean;
  need_phone_number?: boolean;
  need_email?: boolean;
  need_shipping_address?: boolean;
  send_phone_number_to_provider?: boolean;
  send_email_to_provider?: boolean;
  is_flexible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TelegramService {
  private window;
  tgUser: WebAppUser;
  tg;

  constructor(
    @Inject(DOCUMENT) private _document,
    private readonly http: HttpClient,
  ) {
    this.window = this._document.defaultView;
    this.tg = this.window?.Telegram?.WebApp;
    this.tgUser = this.window?.Telegram?.WebApp?.initDataUnsafe?.user;
  }

  get MainButton(): TgButton {
    return this.tg.MainButton;
  }

  get BackButton(): TgButton {
    return this.tg.BackButton;
  }

  getTgUser(): WebAppUser | null {
    return this.window?.Telegram?.WebApp?.initDataUnsafe?.user || null;
  }

  sendData(data: object) {
    this.tg?.sendData(JSON.stringify(data));
  }

  ready() {
    this.tg?.ready();
  }

  openStarsInvoice(invoice: string): void {
    const normalized = this.normalizeInvoiceLink(invoice);
    if (!normalized) {
      this.showAlert('Invoice link is not configured.');
      return;
    }
    if (typeof this.tg?.openInvoice !== 'function') {
      this.showAlert(this.getTelegramOnlyMessage());
      return;
    }
    this.tg.openInvoice(normalized, (status) => {
      console.log('Stars invoice status:', status);
    });
  }

  async openStarsInvoiceByAmount(
    amount: number,
    locale: string = 'ru',
  ): Promise<void> {
    const normalizedAmount = Number(amount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      this.showAlert('Введите корректную сумму звёзд.');
      return;
    }
    if (typeof this.tg?.openInvoice !== 'function') {
      this.showAlert(this.getTelegramOnlyMessage());
      return;
    }

    const invoiceLink = await this.createStarsInvoiceLink(
      normalizedAmount,
      locale,
    );
    if (!invoiceLink) {
      return;
    }

    this.openStarsInvoice(invoiceLink);
  }

  showAlert(message: string): void {
    if (typeof this.tg?.showAlert === 'function') {
      this.tg.showAlert(message);
      return;
    }
    this.showBrowserAlert(message);
  }

  showBrowserAlert(message: string): void {
    if (typeof this.window?.alert === 'function') {
      this.window.alert(message);
    }
  }

  private async createStarsInvoiceLink(
    amount: number,
    locale: string,
  ): Promise<string | null> {
    const baseUrl = environment.apiUrl || '';
    const url = `${baseUrl.replace(/\/$/, '')}/telegram/create-invoice-link`;

    try {
      const response = await firstValueFrom(
        this.http.post<{
          success: boolean;
          data?: { invoiceLink?: string };
          error?: string;
        }>(url, {
          amount,
          locale,
        }),
      );

      if (!response?.success || !response?.data?.invoiceLink) {
        this.showAlert(response?.error || 'Не удалось создать инвойс.');
        return null;
      }

      return response.data.invoiceLink || null;
    } catch (error) {
      console.error('Failed to create Stars invoice link', error);
      this.showAlert('Не удалось создать инвойс. Попробуйте позже.');
      return null;
    }
  }

  public getTelegramOnlyMessage(): string {
    return 'Доступно в через Тлеграмм бот https://t.me/My3EgoBot';
  }

  private normalizeInvoiceLink(value: string): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `https://t.me/invoice/${trimmed}`;
  }
}
